import React, { createContext, useContext, useEffect, useState } from "react";

import useGetUserPermissions from "~/hooks/useGetUserPermissions";
import { permissionAliases } from "~/types/Permissions";
import type { PermissionKey } from "~/types/Permissions";

const STORAGE_KEY = "user_permissions";

interface IPermissionContext {
  permissions: Record<PermissionKey, boolean>;
  hasPermission: (key: PermissionKey) => boolean;
  hasSomePermission: (keys: PermissionKey[]) => boolean;
  loading: boolean;
  refetch: () => void;
}

const PermissionContext = createContext<IPermissionContext>({
  permissions: {} as Record<PermissionKey, boolean>,
  hasPermission: () => false,
  hasSomePermission: () => false,
  loading: true,
  refetch: () => {},
});

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [permissions, setPermissions] = useState<
    Record<PermissionKey, boolean>
  >({} as Record<PermissionKey, boolean>);
  const [loading, setLoading] = useState(true);

  const { data, refetch, isLoading } = useGetUserPermissions();

  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      if (cached) {
        const { data: cachedData } = JSON.parse(cached);
        setPermissions(cachedData);
        setLoading(false);
        return;
      }
    } catch (e) {
      console.error("Ошибка чтения кеша permissions", e);
      sessionStorage.removeItem(STORAGE_KEY);
    }

    refetch();
  }, []);

  useEffect(() => {
    if (data?.permissions) {
      const typedPermissions = data.permissions as Record<
        PermissionKey,
        boolean
      >;
      setPermissions(typedPermissions);
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ data: typedPermissions })
      );
      setLoading(false);
    }
  }, [data]);

  const hasPermission = (key: PermissionKey) => {
    const mappedKey = permissionAliases[key] ?? key;
    return !!permissions[mappedKey as PermissionKey];
  };

  // Проверка, что есть хоть какое-то право из переданных
  const hasSomePermission = (keys: PermissionKey[]) => {
    return keys.some(key => hasPermission(key));
  }

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        hasPermission,
        hasSomePermission,
        loading: loading || isLoading,
        refetch,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionContext);
